import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { ExternalLink, Send, Flame, CheckCircle, XCircle, Users, Trophy, Target, Crown, Star, Coins, Plus, PencilLine, MessageCircle, X } from 'lucide-react';
import ProfileSettingsModal from './ProfileSettingsModal';
import { resolveProfilePic, handleProfilePicError } from '../lib/avatar';
import { useNavigate } from 'react-router-dom';

const PROFILE_MODAL_BACKGROUND_COLORS = [
  '#f2619c', // Raspberry Rose
  '#a855f7', // Purple
  '#38bdf8', // Sky Blue
  '#34d399', // Green
  '#fb7185', // Pink Rose
  '#f59e0b', // Orange
  '#c084fc', // Light Purple
  '#60a5fa', // Blue
];

const getRandomProfileModalBackground = () => (
  PROFILE_MODAL_BACKGROUND_COLORS[
    Math.floor(Math.random() * PROFILE_MODAL_BACKGROUND_COLORS.length)
  ]
);

const ProfileModal = ({ isOpen, onClose, profileData, onTopUp }) => {
  const { user, refreshCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [modalBackgroundColor, setModalBackgroundColor] = useState(
    getRandomProfileModalBackground
  );
  const wasOpenRef = useRef(false);
  const profile = profileData || user;
  const profileUserId = profile?.userId || profile?.id;

  // Pick one background color per modal session: only on the closed->open
  // transition, never on re-renders while already open (data refreshes,
  // stat updates, etc. must not reshuffle the color mid-session).
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setModalBackgroundColor(getRandomProfileModalBackground());
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Whenever the user's own profile modal is opened, pull the latest stats
  // (credits, profile score, streak, wins, etc.) directly from the backend
  // so admin-side updates are reflected immediately — not stale cached data.
  useEffect(() => {
    if (!isOpen) return;

    if (!profileData && typeof refreshCurrentUser === 'function') {
      refreshCurrentUser();
    }
  }, [isOpen, profileData, refreshCurrentUser]);

  if (!profile) return null;

  const telegramId = (profile.telegramId || '').trim();
  const sanitizedTelegramId = telegramId
    .replace(/^https?:\/\/(t\.me|telegram\.me)\//i, '')
    .replace(/^@/, '');
  const hasTelegramId = Boolean(sanitizedTelegramId);
  const telegramLink = hasTelegramId ? `https://t.me/${sanitizedTelegramId}` : '';
  const telegramButtonLabel = hasTelegramId ? `@${sanitizedTelegramId}` : 'Add Telegram';
  const showOwnProfileActions = !profileData;

  const handleMessageAction = () => {
    if (!profileUserId || profileUserId === user?.id) return;
    onClose();
    navigate(`/messages/${profileUserId}`);
  };

  const handleTelegramAction = () => {
    if (hasTelegramId) {
      window.open(telegramLink, '_blank', 'noopener,noreferrer');
      return;
    }

    if (showOwnProfileActions) {
      setIsSettingsOpen(true);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          hideCloseButton
          className="harday-profile-modal inset-0 translate-x-0 translate-y-0 w-screen max-w-none rounded-none sm:rounded-none border-0 p-0 gap-0 flex flex-col overflow-hidden"
          style={{ backgroundColor: modalBackgroundColor }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="harday-profile-modal-close absolute right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/70 text-black/70 opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>

          <div className="harday-profile-modal-scroll flex-1 overflow-y-auto px-6">
          <DialogHeader className="text-left sm:text-left">
            <DialogTitle className="text-left font-['Outfit'] text-2xl text-[#000000]">
              Profile
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-3">
            {/* Header: avatar top-left, name/username + actions to the right */}
            <div className="flex items-start gap-3">
              <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white flex-shrink-0">
                <img
                  src={resolveProfilePic(profile.profilePic)}
                  alt={profile.name}
                  onError={handleProfilePicError}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-[#000000] font-['Outfit'] leading-tight truncate">
                  {profile.name}
                </h3>
                <p className="text-sm text-[#666666] font-['DM_Sans'] truncate">
                  {profile.username}
                </p>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {showOwnProfileActions && (
                    <button
                      type="button"
                      onClick={() => setIsSettingsOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#9333EA]/20 bg-white/70 px-3 py-1.5 text-xs font-semibold text-[#9333EA]"
                    >
                      <PencilLine className="w-3.5 h-3.5" />
                      Edit Profile
                    </button>
                  )}

                  {!showOwnProfileActions && profileUserId && profileUserId !== user?.id && (
                    <button
                      type="button"
                      onClick={handleMessageAction}
                      data-testid="profile-message-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white"
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      Message
                    </button>
                  )}

                  {(showOwnProfileActions || hasTelegramId) && (
                    <button
                      type="button"
                      onClick={handleTelegramAction}
                      data-testid="telegram-id-btn"
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors min-w-0 max-w-full ${
                        hasTelegramId
                          ? 'bg-[#0088cc] text-white hover:bg-[#0077b5]'
                          : 'bg-white/85 text-[#0088cc] border border-[#0088cc]/30 hover:bg-white'
                      }`}
                    >
                      <Send className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{telegramButtonLabel}</span>
                      {hasTelegramId && <ExternalLink className="w-3 h-3 opacity-70 flex-shrink-0" />}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Score (left) + Credits Left (right), side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div className="glass-panel rounded-[16px] p-4 flex flex-col justify-center">
                <p className="text-xs text-[#666666] mb-1 font-['DM_Sans']">Profile Score</p>
                <p className="text-3xl font-bold text-gradient font-['Outfit'] leading-none">
                  {Math.floor(profile.profileScore || 0)}
                </p>
              </div>

              <div className="glass-panel rounded-[16px] p-4 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Coins className="w-6 h-6 text-[#FBBF24] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Credits Left</p>
                    <p className="text-2xl font-bold text-[#000000] font-['Outfit'] leading-none">
                      {profile.credits || 0}
                    </p>
                  </div>
                </div>
                {showOwnProfileActions && onTopUp && (
                  <button
                    onClick={() => { onClose(); onTopUp(); }}
                    data-testid="profile-topup-btn"
                    className="w-8 h-8 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] flex items-center justify-center flex-shrink-0"
                  >
                    <Plus className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
            </div>

            <div className="glass-panel rounded-[16px] p-4">
              <h4 className="text-sm font-semibold text-[#000000] mb-2 font-['Outfit']">Stats</h4>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <Flame className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-lg font-bold text-[#000000] font-['Outfit']">{profile.streak}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Day Streak</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <Target className="w-5 h-5 text-[#9333EA]" />
                  <div>
                    <p className="text-lg font-bold text-[#000000] font-['Outfit']">{profile.totalPuzzlesSolved}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Completed</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-lg font-bold text-green-600 font-['Outfit']">{profile.rightAnswers || 0}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Right Answers</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-lg font-bold text-red-500 font-['Outfit']">{profile.wrongAnswers || 0}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Wrong Answers</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <Trophy className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <p className="text-lg font-bold text-[#000000] font-['Outfit']">
                      {profile.totalPuzzlesSolved > 0
                        ? Math.round(((profile.rightAnswers || 0) / profile.totalPuzzlesSolved) * 100)
                        : 0}%
                    </p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Accuracy</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <Users className="w-5 h-5 text-[#EC4899]" />
                  <div>
                    <p className="text-lg font-bold text-[#000000] font-['Outfit']">{profile.totalFollowers || profile.referrals?.total || 0}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Followers</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <Crown className="w-5 h-5 text-[#9333EA]" />
                  <div>
                    <p className="text-lg font-bold text-gradient font-['Outfit']">{profile.poolWins || 0}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Legend Hits</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-white/50 rounded-[12px]">
                  <Star className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <p className="text-lg font-bold text-[#000000] font-['Outfit']">{profile.downlineWins || 0}</p>
                    <p className="text-xs text-[#666666] font-['DM_Sans']">Legends in Friends</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {showOwnProfileActions && <ProfileSettingsModal isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} />}
    </>
  );
};

export default ProfileModal;
